import {
  type ProblemSearchRow,
  searchProblemRows,
} from '@/lib/problems/problem-search'
import type { LibraryItem } from './filter-engine'

/**
 * Fuzzy free-text search over the loaded library corpus (LG-362 §6).
 *
 * Delegates to `searchProblemRows` rather than configuring Fuse here:
 * `lib/problems/problem-search.ts` (LG-448) is the canonical search
 * configuration — weights, threshold, and the numeric-query→exact-external-id
 * rule — and search parity across surfaces depends on nobody forking it.
 * Caveat until LG-481 lands: the admin master problem table still carries
 * its own pre-extraction copy of this config (`use-table-filters.ts`,
 * `use-problem-search.ts`), so tuning the canonical module does not yet move
 * that surface.
 *
 * Returns `null` for a blank query ("no search active" — the caller shows
 * every row), otherwise the set of matching item ids. A set rather than an
 * ordered list because `selectLibraryItems` interleaves search with the
 * filter tree and then applies the coach's own sorts — mirroring the admin
 * master table, which also filters by match membership and keeps its own
 * ordering.
 *
 * Searched fields are the admin table's: title, external id, source label.
 * The old `.includes()` haystack also swept `topic`/`subtopic` and `plans` —
 * those are deliberately no longer searched, and stayed out when §1 made the
 * taxonomy real (LG-542): both now have a dedicated filter, and adding them
 * back here would fork the canonical search config this delegates to.
 */
export function matchLibraryItems(
  items: readonly LibraryItem[],
  query: string,
): ReadonlySet<string> | null {
  if (query.trim() === '') return null

  const rows = items.map(
    (item): ProblemSearchRow => ({
      id: item.id,
      title: item.title,
      slug: null,
      externalId: item.extId,
      // Every live source rides along with its own id (LG-362 §3): the title
      // displays the WORN source's id, so any id the coach can see — or a
      // client can quote from LeetCode etc. — must be searchable. The shared
      // engine already exact-matches `sources[].externalId` for numeric
      // queries; indexing all of them keeps search pick-independent (no
      // index rebuild per pick).
      sources: [
        ...(item.source === undefined
          ? []
          : [{ title: null, source: item.source, externalId: null }]),
        ...(item.sources ?? []).map((source) => ({
          title: null,
          source: source.displayName,
          externalId: source.externalId,
        })),
      ],
    }),
  )

  return new Set(
    searchProblemRows(rows, query, rows.length).map((row) => row.id),
  )
}
